/**
 * React hooks for analytics tracking
 */

import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { CONSENT_APPLIED_EVENT } from '../consent'
import { trackPageView, trackScrollDepth, trackTimeOnPage } from '../analytics'

/**
 * Track page views on route changes
 */
export function usePageViews() {
  const location = useLocation()
  const [consentAppliedTick, setConsentAppliedTick] = useState(0)

  useEffect(() => {
    const handleConsentApplied = () => setConsentAppliedTick((tick) => tick + 1)

    window.addEventListener(CONSENT_APPLIED_EVENT, handleConsentApplied)
    return () => window.removeEventListener(CONSENT_APPLIED_EVENT, handleConsentApplied)
  }, [])
  
  useEffect(() => {
    const title = document.title
    const path = location.pathname + location.search
    
    trackPageView(path, title)
  }, [location, consentAppliedTick])
}

/**
 * Track scroll depth (25%, 50%, 75%, 90%, 100%)
 */
export function useScrollTracking() {
  const location = useLocation()
  const trackedDepths = useRef<Set<number>>(new Set())
  
  useEffect(() => {
    // Reset tracked depths on route change
    trackedDepths.current.clear()
    
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrolled = window.scrollY
      const scrollPercent = Math.round((scrolled / scrollHeight) * 100)
      
      // Track milestones: 25%, 50%, 75%, 90%, 100%
      const milestones = [25, 50, 75, 90, 100]
      
      for (const milestone of milestones) {
        if (scrollPercent >= milestone && !trackedDepths.current.has(milestone)) {
          trackedDepths.current.add(milestone)
          
          trackScrollDepth(milestone, location.pathname, document.title)
        }
      }
    }
    
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [location])
}

/**
 * Track time spent on page
 * Reports every 30 seconds and on unmount
 */
export function useTimeOnPageTracking() {
  const location = useLocation()
  const startTime = useRef<number>(Date.now())
  const lastReported = useRef<number>(0)
  
  useEffect(() => {
    startTime.current = Date.now()
    lastReported.current = 0
    
    const reportTime = () => {
      const timeOnPage = Math.floor((Date.now() - startTime.current) / 1000)
      
      // Only report if at least 10 seconds have passed since last report
      if (timeOnPage - lastReported.current >= 10) {
        trackTimeOnPage(timeOnPage, location.pathname, document.title)
        lastReported.current = timeOnPage
      }
    }
    
    // Report every 30 seconds
    const interval = setInterval(reportTime, 30000)
    
    // Report on page leave
    return () => {
      clearInterval(interval)
      reportTime()
    }
  }, [location])
}

/**
 * Combined hook for all page tracking
 */
export function useAnalytics() {
  usePageViews()
  useScrollTracking()
  useTimeOnPageTracking()
}
